import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Loader2, ArrowUpRight, Lock } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
    const { login, user, loading } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    if (loading) return <FullscreenLoader />;
    if (user) return <Navigate to="/admin" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error("Email and password required");
            return;
        }
        setBusy(true);
        try {
            await login(email, password);
            toast.success("Welcome back.");
            navigate("/admin", { replace: true });
        } catch (err) {
            const msg =
                err?.response?.data?.detail || "Invalid email or password";
            toast.error(typeof msg === "string" ? msg : "Login failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <main
            data-testid="admin-login-page"
            className="min-h-screen bg-ink text-bone flex items-center justify-center px-6 grain"
        >
            <div className="w-full max-w-md">
                <a
                    href="/"
                    className="flex items-center gap-3 mb-12"
                    data-testid="login-brand"
                >
                    <img
                        src="/logo.png"
                        alt="Aximo Studios"
                        className="h-12 w-auto"
                    />
                </a>

                <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-ember mb-6">
                    <Lock className="w-3.5 h-3.5" />
                    Studio admin
                </div>
                <h1 className="font-display text-bone text-5xl tracking-tightest leading-[1] mb-10">
                    Sign in to
                    <br />
                    <span className="font-serif-i italic text-ember">
                        the briefcase.
                    </span>
                </h1>

                <form
                    onSubmit={onSubmit}
                    className="space-y-6 border border-white/10 bg-ink-800/40 p-8"
                    data-testid="admin-login-form"
                >
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                            Email
                        </span>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="admin@aximo.studio"
                            data-testid="admin-input-email"
                            className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-3 text-bone placeholder:text-bone-500 font-display text-lg tracking-tight transition-colors"
                            autoFocus
                        />
                    </label>
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                            Password
                        </span>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            data-testid="admin-input-password"
                            className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-3 text-bone placeholder:text-bone-500 font-display text-lg tracking-tight transition-colors"
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="admin-login-submit"
                        className="btn-ember w-full inline-flex items-center justify-between h-12 px-5 text-sm font-medium tracking-wide disabled:opacity-60"
                    >
                        {busy ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <span>Sign in</span>
                        )}
                        <ArrowUpRight className="w-4 h-4" />
                    </button>
                </form>

                <a
                    href="/"
                    className="mt-10 block text-xs uppercase tracking-[0.25em] text-bone-500 hover:text-ember transition-colors"
                >
                    ← Back to studio
                </a>
            </div>
        </main>
    );
}

function FullscreenLoader() {
    return (
        <div className="min-h-screen bg-ink flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-ember" />
        </div>
    );
}
